import i18n from '../../i18n';


/**
 * Created by kimmm on 2018/9/17.
 *
 * Utils 工具函数
 */

/**
 * 检测传入的参数类型
 * @param obj {All}	需要进行参数检测的对象
 * @return {String} 所属类型字符串
 */
function typeOf (obj) {
    const toString = Object.prototype.toString
    const map = {
        '[object Boolean]': 'boolean',
        '[object Number]': 'number',
        '[object String]': 'string',
        '[object Function]': 'function',
        '[object Array]': 'array',
        '[object Date]': 'date',
        '[object RegExp]': 'regExp',
        '[object Undefined]': 'undefined',
        '[object Null]': 'null',
        '[object Object]': 'object'
    }
    return map[toString.call(obj)]
}

function isNumber (obj) {
    return typeOf(obj) === 'number' && !isNaN(obj)
}

function isNaN (obj) {
    return obj.toString() === 'NaN'
}

function isString (obj) {
    return typeOf(obj) === 'string'
}

function isFunction (obj) {
    return typeOf(obj) === 'function'
}

function isArray (obj) {
    return typeOf(obj) === 'array'
}

function isObject (obj) {
    return typeOf(obj) === 'object'
}

function isUndefined (obj) {
    return typeOf(obj) === 'undefined'
}

/**
 * 判断一个元素是否在目标数组中
 * @param target   待判断的元素
 * @param arr      目标数组
 * @param cb      【可选】回调函数，接受这个 target 在数组中的索引
 * @returns {boolean}
 */
function inArray(target, arr, cb) {
    for (let i = 0, item; i < arr.length; i++) {
        item = arr[i]
        if (target === item) {
            cb && cb(i)
            return true
        }
    }
    return false
}

/**
 * 观察者类
 */
class Emitter {
    constructor () {
        this.pool = {
            loaded: [],
            unloaded: [],
            allLoaded: []
        }
    }

    on (type, handler) {
        if (!isFunction(handler)) return new Error('DynamicScript-error: DynamicScript.prototype.on 监听的必须是一个函数')
        type = type.toString()
        let handlerList = this.pool[type]
        handlerList == null && (handlerList = this.pool[type] = [])
        handlerList.push(handler)
    }

    off (type, handler) {
        type = type.toString()
        let handlerList = this.pool[type]
        if (handler == null) {
            handlerList[type] = void (0)
        } else {
            for (let i = 0, fn; i < handlerList.length; i++) {
                fn = handlerList[i]
                if (fn === handler) {
                    handlerList.splice(i, 1)
                    break
                }
            }
        }
    }

    trigger (type, data) {
        type = type.toString()
        let handlerList = this.pool[type]
        for (let i = 0, fn; i < handlerList.length; i++) {
            fn = handlerList[i]
            fn(data)
        }
    }
}

// 动态脚本操作类
class DynamicScript extends Emitter {
    constructor () {
        super()
        this._generatorScriptDOM()
        this.loading = false // 正在加载的标识符，未完成之前不能加载别的脚本
        this.existScriptsList = this.getExistScriptSrcList() // 页面已存在的脚本列表
        this.needLoadScriptsPool = [] // 需要加载的脚本池
        this.needUnloadScriptPool = [] // 需要卸载的脚本池
    }

    /**
     * 生成 script 节点
     * @private
     */
    _generatorScriptDOM () {
        const self = this
        const scriptDOM = this.scriptDOM = document.createElement('script')
        scriptDOM.type = 'text/javascript'
        scriptDOM.onload = scriptDOM.onreadystatechange = function (e) {
            if(!this.readyState || this.readyState=='loaded' || this.readyState=='complete') self._loadSuccess(e)
        }
    }

    _loadSuccess (e) {
        const scriptDOM = this.scriptDOM
        let nextSrc = ''
        let needLoadScriptsPool = this.needLoadScriptsPool
        this.loading = false
        // 触发一次单次加载完成事件
        super.trigger('loaded', scriptDOM)
        // 拿当前加载成功的 src 去遍历脚本池，找到就剔除，代表改脚本已加载完成
        inArray(scriptDOM.src, needLoadScriptsPool, index => needLoadScriptsPool.splice(index, 1))
        // 重新生成 script 节点
        this._generatorScriptDOM()
        // 取脚本池里的下一个源进行加载
        nextSrc = needLoadScriptsPool[0]
        nextSrc != null && this.load(nextSrc)
        // 更新页面已存在的脚本列表
        this.existScriptsList = this.getExistScriptSrcList()
        // 检测 needLoadScriptsPool 脚本池，为空就触发全部加载完成事件
        if (needLoadScriptsPool.length === 0) super.trigger('allLoaded')
    }

    /**
     * 获取页面已存在的脚本列表
     * @returns {Array}
     */
    getExistScriptSrcList () {
        let list = [...document.getElementsByTagName('script')]
        return list.map(script => script.src)
    }

    /**
     * 单源加载
     * @param src
     */
    load (src) {
        // 还有脚本正在加载，就 return
        if (this.loading) return
        // 开 loading
        this.loading = true
        const scriptDOM = this.scriptDOM
        // 如果已经加载过这个脚本源就 return
        if (this.checkExist(src)) return
        scriptDOM.src = String(src)
        document.body.appendChild(scriptDOM)
    }

    /**
     * 单源卸载
     * @param src
     */
    unload (src) {
        const scriptDOMList = [...document.getElementsByTagName('script')]
        scriptDOMList.forEach(dom => {
            if (dom.src === String(src)) {
                document.body.removeChild(dom)
            }
        })
    }

    /**
     * 批量加载
     * @param list { Array } 由脚本源 string 组成的数组
     */
    batchload(list) {
        let scripts = this.needLoadScriptsPool = [...this.needLoadScriptsPool, ...list]
        // 从第一个 src 入口开始，后续的加载在 _loadSuccess 里调用
        this.load(String(scripts[0]))
    }

    /**
     * 批量卸载
     * @param list { Array } 由脚本源 string 组成的数组
     */
    batchUnload(list) {
        let scripts = this.needUnloadScriptPool = [...this.needUnloadScriptPool, ...list]
        scripts.forEach(src => {
            this.unload(String(src))
        })
        this.needUnloadScriptPool = []
        // 触发全部卸载事件
        this.trigger('allLoaded')
    }

    /**
     * 检测传入的 src 脚本源是否已经被加载并被添加至 DOM 中
     * @param src
     * @returns {boolean}
     */
    checkExist(src) {
        const existScriptsList = this.existScriptsList
        let [ item, result ] = [ null, false ]
        for (let i = 0; i < existScriptsList.length; i++) {
            item = existScriptsList[i]
            if (item.src === String(src)) {
                result = true
                break
            }
        }
        return result
    }
}


/**
 * 数学运算保留精度
 *
 * floatTool 包含加减乘除四个方法，能确保浮点数运算不丢失精度
 *
 * 我们知道计算机编程语言里浮点数计算会存在精度丢失问题（或称舍入误差），其根本原因是二进制和实现位数限制有些数无法有限表示
 * 以下是十进制小数对应的二进制表示
 *      0.1 >> 0.0001 1001 1001 1001…（1001无限循环）
 *      0.2 >> 0.0011 0011 0011 0011…（0011无限循环）
 * 计算机里每种数据类型的存储是一个有限宽度，比如 JavaScript 使用 64 位存储数字类型，因此超出的会舍去。舍去的部分就是精度丢失的部分。
 *
 * ** method **
 *  add / subtract / multiply /divide
 *
 * ** explame **
 *  0.1 + 0.2 == 0.30000000000000004 （多了 0.00000000000004）
 *  0.2 + 0.4 == 0.6000000000000001  （多了 0.0000000000001）
 *  19.9 * 100 == 1989.9999999999998 （少了 0.0000000000002）
 *
 * floatObj.add(0.1, 0.2) >> 0.3
 * floatObj.multiply(19.9, 100) >> 1990
 *
 */
const floatTool = (() => {

    /*
     * 判断obj是否为一个整数
     */
    function isInteger(obj) {
        return Math.floor(obj) === obj
    }

    /*
     * 将一个浮点数转成整数，返回整数和倍数。如 3.14 >> 314，倍数是 100
     * @param floatNum {number} 小数
     * @return {object}
     *   {times:100, num: 314}
     */
    function toInteger(floatNum) {
        var ret = {times: 1, num: 0}
        if (isInteger(floatNum)) {
            ret.num = floatNum
            return ret
        }
        var strfi  = floatNum + ''
        var dotPos = strfi.indexOf('.')
        var len    = strfi.substr(dotPos+1).length
        var times  = Math.pow(10, len)
        var intNum = parseInt(floatNum * times + 0.5, 10)
        ret.times  = times
        ret.num    = intNum
        return ret
    }

    /*
     * 核心方法，实现加减乘除运算，确保不丢失精度
     * 思路：把小数放大为整数（乘），进行算术运算，再缩小为小数（除）
     *
     * @param a {number} 运算数1
     * @param b {number} 运算数2
     * @param digits {number} 精度，保留的小数点数，比如 2, 即保留为两位小数
     * @param op {string} 运算类型，有加减乘除（add/subtract/multiply/divide）
     *
     */
    function operation(a, b, op) {
        var o1 = toInteger(a)
        var o2 = toInteger(b)
        var n1 = o1.num
        var n2 = o2.num
        var t1 = o1.times
        var t2 = o2.times
        var max = t1 > t2 ? t1 : t2
        var result = null
        switch (op) {
            case 'add':
                if (t1 === t2) { // 两个小数位数相同
                    result = n1 + n2
                } else if (t1 > t2) { // o1 小数位 大于 o2
                    result = n1 + n2 * (t1 / t2)
                } else { // o1 小数位 小于 o2
                    result = n1 * (t2 / t1) + n2
                }
                return result / max
            case 'subtract':
                if (t1 === t2) {
                    result = n1 - n2
                } else if (t1 > t2) {
                    result = n1 - n2 * (t1 / t2)
                } else {
                    result = n1 * (t2 / t1) - n2
                }
                return result / max
            case 'multiply':
                result = (n1 * n2) / (t1 * t2)
                return result
            case 'divide':
                return result = function() {
                    var r1 = n1 / n2
                    var r2 = t2 / t1
                    return operation(r1, r2, 'multiply')
                }()
        }
    }

    // 加减乘除的四个接口
    function add(a, b) {
        return operation(a, b, 'add')
    }
    function subtract(a, b) {
        return operation(a, b, 'subtract')
    }
    function multiply(a, b) {
        return operation(a, b, 'multiply')
    }
    function divide(a, b) {
        return operation(a, b, 'divide')
    }

    // exports
    return {
        add: add,
        subtract: subtract,
        multiply: multiply,
        divide: divide
    }
})()


/**
 * 对象空属性用预制位字符串代替
 * @param obj {Object} 处理对象
 * @param extend {Array / Object} 存储 KeyString 的数组，或者直接是示例对象，用于检测额外属性，扩展 obj 对象
 * @param preform {String} 预制位
 * @returns {{}}
 */
function objPreformReplace( obj = {}, extend = [], preform = '--') {
    for (let k in obj) {
        // 如果 value 为空或为 Null / undefined，就用 preform 代替这个字段
        if (obj[k] === '' || obj[k] == null) obj[k] = preform
    }
    if (typeOf(extend) === 'object') {
        for (let k in extend) {
            if (obj[k] === '' || obj[k] == null) obj[k] = preform
        }
    } else if (typeOf(extend) === 'array') {
        extend.forEach(k => {
            if (obj[k] === '' || obj[k] == null) obj[k] = preform
        })
    }
    return obj
}

/**
 * 判断一个DOM元素是否包裹在另一个DOM元素中【父子关系或者层级嵌套都可以】
 * @param  {Object} DOM         事件对象中的event.target/或者是需要检测的DOM元素
 * @param  {Object} targetDOM   作为限制范围的DOM元素
 * @return {Boolean}            true----是包裹关系，false----不是包裹关系
 */
function inTargetArea (DOM, targetDOM) {
    let parent = DOM.parentNode
    while (parent != null) {
        if (parent === targetDOM) return true
        DOM = parent
        parent = DOM.parentNode
    }
    return false
}


/**
 * 数字输入过滤函数，写的比较繁琐，后面要优化
 * @param value         输入数据
 * @param num   需要保留的小数位数，可不传
 * @returns {String}    过滤完成后的数据
 */
function numberFilter(value, num) {
    let input = value.split('') || [],
        fractionNum = !isNaN(num - 0) ? num : 0,    // 小数位数
        pointFlag = true,       // 是否能够输入小数点
        pointIndex,             // 小数点位置
        output = [];            // 输出数组
    input.forEach(function(value,key){
        if (!isNaN(value-0)) {    // 输入的为数字
            if (fractionNum) {   // 允许小数情况
                if (pointFlag) {  // 能够输入小数
                    output.push(value);
                } else {     // 已经输入过，不能再输入小数
                    if (key <= fractionNum + pointIndex) {
                        output.push(value);
                    }
                }
            } else {  // 不允许小数
                if (key == 0 && value == 0) {

                }else{
                    output.push(value);
                }
            }
        } else if (value === '.'){    // 输入为'.'
            if (pointFlag && fractionNum) {
                if(key === 0){
                    output.push('0','.');
                }else if(key > 0){
                    output.push('.');
                }
                pointIndex = key;
                pointFlag = false;
            }
        }
    });
    output = output.join('');
    return output;

    /**
     * 判断NaN
     * @param target 需要进行检测的目标对象
     */
    function isNaN(target) {
        return target.toString() === 'NaN'
    }
}

/**
 * 添加某个 className
 * @param dom
 * @param className
 */
function addClass (dom, className) {
    className = String(className)
    const flag = dom.className.indexOf(className)
    if (flag === -1)
        dom.className = `${dom.className} ${className}`
}

/**
 * 移除某个 className
 * @param dom
 * @param className
 */
function removeClass(dom, className) {
    className = String(className)
    const flag = dom.className.indexOf(className)
    if (flag > -1)
        dom.className = dom.className.replace(className, '').trim()
}

/**
 * 检测某一个 DOM 元素是否包含某个 className
 * @param dom
 * @param className
 */
function hasClass(dom, className) {
    className = String(className)
    const classNameList = dom.className.split(' ')
    for (let i = 0; i < classNameList.length; i++) {
        if (className === classNameList[i]) return true
    }
    return false
}

/**
 * 数字单位转换 元/万元/亿元/万亿
 * @param num
 * @returns {{num: number, unit: string}}
 */
function unitConvert (num) {
    var moneyUnits = i18n.t('message.saleTarget_money_units');
    var dividend = 10000
    var curentNum = num //转换数字
    var curentUnit = moneyUnits[0] //转换单位
    for (var i = 0; i <4; i++) {
        curentUnit = moneyUnits[i]
        if(strNumSize(curentNum) < 5) break;
        curentNum = curentNum / dividend
    }
    var m = {num: 0, unit: ""}
    m.num = curentNum.toFixed(2)
    m.unit = curentUnit
    return m

    function strNumSize (tempNum) {
        var stringNum = tempNum.toString()
        var index = stringNum.indexOf(".")
        var newNum = stringNum
        if( index != -1 ) {
            newNum = stringNum.substring(0,index)
        }
        return newNum.length
    }
}


/**
 * 节流器
 */
class Throttle {
    constructor(interval = 200) {
        this._timeoutId = null;
        this._interval = interval;
        this.funcPool = [];
    }

    add(...funcList) {
        this.funcPool.push(...funcList);
    }

    remove(func) {
        this.funcPool = this.funcPool.filter(item => item !== func);
    }

    handle(...args) {
        const funcPool = this.funcPool;
        if (this._timeoutId != null) clearTimeout(this._timeoutId);
        this._timeoutId = setTimeout(() => {
            funcPool.forEach(func => {
                func(...args);
            });
        }, this._interval);
    }
}



/*
 * 获取目标元素相对于某一父元素的位置
 * tips: 如果该元素是隐藏的，则 offset 相关属性无效，为0
 * @param el {DOMobject}    目标（子）DOM元素
 * @param target {DOMobject} 父DOM元素
 * @return object 包括 offsetLeft 和 offsetTop
 *
 */
function getOffsetToParentNode (el, target) {
    if (!inTargetArea(el, target)) return null
    const [ targetOffsetWidth, targetOffsetHeight ] = [ target.offsetWidth, target.offsetHeight ]
    const [ elOffsetWidth, elOffsetHeight ] = [ el.offsetWidth, el.offsetHeight ]
    let [ top, left, right, bottom ] = [ 0, 0, 0, 0 ]
    let parent = el.parentNode

    while (parent != null) {
        top += el.offsetTop
        left += el.offsetLeft
        el = parent
        parent = el.parentNode
    }
    right = targetOffsetWidth - elOffsetWidth - left
    bottom = targetOffsetHeight - elOffsetHeight - top
    return {
        top,
        left,
        right,
        bottom
    }
}

/*
 * 获取某个 DOM 元素相对页面的offset
 * @param el {object} 目标元素
 * @return object 包括offsetLeft和offsetTop
 */
function getOffset (el) {
    const doc = document.documentElement
    let [ left, top, right, bottom, parent ] = [0, 0, 0, 0, el.parentNode]
    while (parent != null) {
        left += el.offsetLeft
        top += el.offsetTop
        el = parent
        parent = el.parentNode
    }
    right = doc.offsetWidth - el.offsetWidth - left
    bottom = doc.offsetHeight - el.offsetHeight - top
    return {
        left,
        top,
        right,
        bottom
    }
}

/*
 * 获取鼠标当前相对于某个元素的位置
 * @param e 	   {object}    原生事件对象
 * @param target {DOMobject} 目标DOM元素
 * @return object 包括offsetLeft和offsetTop
 *
 * Tips:
 * 1.offset 相关属性在 display: none 的元素上失效，为0
 * 2.offsetWidth/offsetHeight 包括border-width，clientWidth/clientHeight不包括border-width，只是可见区域而已
 * 3.offsetLeft/offsetTop 是从当前元素边框外缘开始算，一直到定位父元素的距离，clientLeft/clientTop其实就是border-width
 */
function getOffsetInElement (e, target) {
    let currentDOM = e.target || e.toElement
    if (!inTargetArea(currentDOM, target)) return null
    let left, top, right, bottom
    const { left: x, top: y } = getOffset(target)
    left = e.clientX - x
    top = e.clientY - y
    right = target.offsetWidth - left
    bottom = target.offsetHeight - top
    return { top, left, right, bottom }
}

/**
 * 数字四舍五入
 * @param number        需要操作的数字
 * @param precision     需要保留位数
 * @returns {number}    返回的数
 */
function round(number, precision) {
    return Math.round(+number + 'e' + precision) / Math.pow(10, precision);
    //same as:
    //return Number(Math.round(+number + 'e' + precision) + 'e-' + precision);
}

/**
 * 轮询函数
 * @param judgeFunc { Function }    判断函数，每次轮询通过这个判断函数执行结果，来判断是否完成轮询
 * @param callBack  { Function }    完成轮询执行的回调
 * @param interval  { Number }      轮询间隔
 * @param list      { like-array }  传入回调函数中的额外参数
 */
function polling(judgeFunc, callBack, interval = 0, ...list) {
    const flag = typeOf(judgeFunc) === 'function' && judgeFunc()
    if (flag) {
        callBack(...list)
    } else {
        setTimeout(() => {
            polling(judgeFunc, callBack, interval, ...list)
        }, interval)
    }
}

/**
 * 获取网页地址栏url的参数
 * @param name {string} url的key
 * @returns {string}
 */
function getUrlParam(name) {
    const queryStr = String(window.location).split('?')[1] || ''
    name = encodeURIComponent(name)
    let arr = new RegExp("(^|&)" + name + "=([^&]*)(&|$)").exec(queryStr);
    if(arr){
        return RegExp.$2;
    }else{
        return '';
    }
}

/**
 * 获取 data- 开头的自定义属性
 * @param  {Object} el   要获取属性的原生DOM元素
 * @param  {String} name 要获取的属性名
 * @param  {String} val  要设置的属性值
 * @return {String}      获取到的属性值【设置值无返回】
 */
function getData(el, name, val) {
    let prefix = 'data-';
    name = prefix + name;
    if (val) {
        el.setAttribute(name, val)
    }else{
        return el.getAttribute(name)
    }
}

/**
 * 去掉字符串中固定字符
 */
function deleteString(str, delStr) {
    return str.replace(delStr);
}

export {
    typeOf,
    isNumber,
    isNaN,
    isString,
    isFunction,
    isArray,
    isObject,
    isUndefined,
    inArray,
    DynamicScript,
    floatTool,
    objPreformReplace,
    numberFilter,
    addClass,
    removeClass,
    hasClass,
    unitConvert,
    Throttle,
    inTargetArea,
    getOffsetToParentNode,
    getOffset,
    getOffsetInElement,
    round,
    polling,
    getUrlParam,
    getData,
    deleteString
}